import { clsx } from 'clsx'
import { type FC } from 'react'
import { type IntlShape, useIntl } from 'react-intl'

import {
  type UserOrderResponseWithFlagsSubOrdersInner,
  type UserSubOrderV2,
} from '@redteclab/api/clients/bully'

import { useGlobalConfigContext } from '../../global-config'
import {
  parcelLabAccessorGetParcelLabDates,
  parcelLabAccessorGetParcelLabDeliveredStatus,
  useApiParcelLabSwrGetOrderStatus,
} from '../../parcel-lab'
import { OrderSubOrderNowIndicator } from '../components/OrderSubOrderNowIndicator'
import { type OrderStatusConfigLabels } from '../model/AccountOverviewLastOrderShippingStatusTypes'
import {
  userSubOrderAccessorGetStatusBasedOnEstimatedDate,
  userSubOrderAccessorIsCancellationRequested,
  userSubOrderAccessorIsNow,
  userSubOrderAccessorIsNowPickupOrder,
  userSubOrderGetNowOrderTimeRange,
} from '../model/userSubOrderAccessor'
import { userSubOrderShippingStatusMessageAndColor } from '../model/userSubOrderShippingStatusMessageAndColor'

const shippingStatus = (
  intl: IntlShape,
): Record<OrderStatusConfigLabels, string> => {
  const shippingLabel: Record<OrderStatusConfigLabels, string> = {
    canceled: intl.formatMessage({
      id: 'account.orders.status.canceled',
    }),
    cancelRequested: intl.formatMessage({
      id: 'account.orders.cancel.status.requested',
    }),
    closed: intl.formatMessage({
      id: 'account.orders.status.closed',
    }),
    completed: intl.formatMessage({
      id: 'account.orders.status.completed',
    }),
    delivered: intl.formatMessage({
      id: 'account.orders.delivered',
    }),
    dispatched: intl.formatMessage({
      id: 'account.orders.status.dispatched',
    }),
    new: intl.formatMessage({ id: 'account.orders.status.new' }),
    processing: intl.formatMessage({
      id: 'account.orders.status.processing',
    }),
    readyForPickup: intl.formatMessage({
      id: 'account.orders.status.readyForPickup',
    }),
    shipped: intl.formatMessage({
      id: 'account.lastOrder.estimatedDelivery.label',
    }),
    waitingForPrepayment: intl.formatMessage({
      id: 'account.orders.status.waitingForPrepayment',
    }),
  }

  return shippingLabel
}

export const OrderSubOrderStatus: FC<{
  includeNowIndicator?: boolean
  subOrder: UserSubOrderV2 | UserOrderResponseWithFlagsSubOrdersInner
}> = ({ includeNowIndicator, subOrder }) => {
  const intl = useIntl()

  const { language } = useGlobalConfigContext()

  const { data } = useApiParcelLabSwrGetOrderStatus(
    language,
    subOrder.carrier?.trackingId,
    subOrder.carrier?.externalCarrierIds?.parcellab,
  )

  const { deliveryDate, estimatedDate } = parcelLabAccessorGetParcelLabDates(
    intl,
    subOrder,
    data,
  )

  const orderDeliveredStatus = parcelLabAccessorGetParcelLabDeliveredStatus(
    subOrder,
    data,
  )

  if (!subOrder.status) {
    return null
  }

  const isNowOrder = userSubOrderAccessorIsNow(subOrder)

  const userSubOrderStatus = userSubOrderAccessorGetStatusBasedOnEstimatedDate({
    estimatedDate,
    orderDeliveredStatus,
    subOrderStatus: subOrder.status,
    timeRange: userSubOrderGetNowOrderTimeRange(subOrder),
  })

  const orderStatus = userSubOrderShippingStatusMessageAndColor({
    deliveryDate,
    isNowOrder,
    orderStatus: userSubOrderAccessorIsCancellationRequested(subOrder)
      ? 'cancelRequested'
      : userSubOrderStatus,
    shippingStatusLabels: shippingStatus(intl),
    timeRange: userSubOrderGetNowOrderTimeRange(subOrder),
  })

  return (
    <div className="text-start text-s">
      <p className={`flex font-medium ${orderStatus.color}`}>
        {orderStatus.message}
      </p>
      {!userSubOrderAccessorIsNowPickupOrder(subOrder) &&
        orderStatus.lastOrderOrderStatus === 'shipped' && (
          <p
            className={clsx('mb-2 font-bold', orderStatus.color)}
            data-qa-id="account-last-order-now-closed"
          >
            {userSubOrderGetNowOrderTimeRange(subOrder)}
          </p>
        )}
      {isNowOrder && includeNowIndicator ? <OrderSubOrderNowIndicator /> : null}
    </div>
  )
}
